import React, { useRef, useState, useEffect } from 'react';
import { Finding, FindingPhoto, FindingSeverity, inspectionsApi, confidenceTier } from '@/api/inspections';
import { Badge, Button, IconButton, Input, Textarea, cn, type BadgeTone } from '@/ui';
import AIDraftBadge from './AIDraftBadge';

interface FindingCardProps {
  finding: Finding;
  inspectionId: string;
  onUpdated: (finding: Finding) => void;
  onDeleted: (findingId: string) => void;
  isNew?: boolean;
  /** AI confidence score 0–1 from the vision analysis run (session-only, not persisted).
   *  Only present for draft photo findings immediately after analysis. */
  confidence?: number;
  /**
   * Issue #44 — open the SaveToLibrarySheet pre-filled from this finding.
   * Two entry points:
   *   • Bookmark icon on accepted vision findings (`!aiDraft && source==='photo'`)
   *   • Auto-trigger after Accept/Edit-and-save when the inspector ticks
   *     "Save to library after accepting" on a draft.
   * The parent owns the sheet state and the finding's section/component context.
   */
  onSaveToLibrary?: (finding: Finding) => void;
}

type SeverityKey = 'safety_concern' | 'recommendation' | 'observation';

interface SeverityConfig {
  label: string;
  tone: BadgeTone;
  /** Left border stripe color — keyed via CSS variable so it composes with opacity. */
  stripe: string;
  help: string;
}

const SEVERITY_CONFIG: Record<SeverityKey, SeverityConfig> = {
  safety_concern: {
    label:  'Safety Concern',
    tone:   'critical',
    stripe: 'bg-sev-critical',
    help:   'Urgent — a defect that could harm the occupant or cause major damage if left unaddressed.',
  },
  recommendation: {
    // Amber palette (sev-minor). `sev-major` aliases to the red danger-500
    // because the internal 4-level model rolls major+critical up to "safety".
    // For the 2-badge client UI, recommendation must read amber, not red.
    label:  'Recommendation',
    tone:   'info',
    stripe: 'bg-sev-minor',
    help:   'Worth addressing — a defect or wear item the buyer should plan to repair but is not urgent.',
  },
  observation: {
    label:  'Observation',
    tone:   'neutral',
    stripe: 'bg-border',
    help:   'FYI — a note about condition or a component worth knowing about, no action required.',
  },
};

const SEVERITY_KEYS: SeverityKey[] = ['safety_concern', 'recommendation', 'observation'];

function severityKey(s: FindingSeverity): SeverityKey {
  return s === null ? 'observation' : s;
}

const FindingCard: React.FC<FindingCardProps> = ({
  finding,
  inspectionId,
  onUpdated,
  onDeleted,
  isNew = false,
  confidence,
  onSaveToLibrary,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(finding.title);
  const [editDescription, setEditDescription] = useState(finding.description ?? '');
  const [editSeverity, setEditSeverity] = useState<FindingSeverity>(finding.severity);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [showPhotoPrompt, setShowPhotoPrompt] = useState(false);
  // Issue #44 — when ticked on the draft accept/edit surface, opens the
  // SaveToLibrarySheet right after the PATCH that flips ai_draft → false.
  const [saveAfterAccept, setSaveAfterAccept] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDraft = finding.aiDraft;
  const canSaveAcceptedToLibrary =
    !isDraft && finding.source === 'photo' && Boolean(onSaveToLibrary);

  useEffect(() => {
    // Skip the "Add photo?" nudge for AI drafts — the inspector should review
    // and accept first; photo workflow becomes available after that.
    if (!isNew || isDraft) return;
    setShowPhotoPrompt(true);
    const timer = setTimeout(() => setShowPhotoPrompt(false), 8000);
    return () => clearTimeout(timer);
  }, [isNew, isDraft]);

  const config = SEVERITY_CONFIG[severityKey(finding.severity)];

  const handleToggle = async () => {
    const result = await inspectionsApi.updateFinding(inspectionId, finding.id, {
      is_included: !finding.isIncluded,
    });
    if (result.success && result.data) {
      onUpdated(result.data);
    }
  };

  const handleSave = async () => {
    if (!editTitle.trim()) return;
    setSaving(true);
    try {
      const result = await inspectionsApi.updateFinding(inspectionId, finding.id, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        severity: editSeverity,
        // Edit-and-save is one of the two ways an AI draft becomes inspector-
        // authored; flip the flag in the same patch so we don't need a second round trip.
        ...(isDraft ? { ai_draft: false as const } : {}),
      });
      if (result.success && result.data) {
        onUpdated(result.data);
        setIsEditing(false);
        if (isDraft && saveAfterAccept && onSaveToLibrary) {
          onSaveToLibrary(result.data);
          setSaveAfterAccept(false);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const result = await inspectionsApi.updateFinding(inspectionId, finding.id, {
        ai_draft: false,
      });
      if (result.success && result.data) {
        onUpdated(result.data);
        if (saveAfterAccept && onSaveToLibrary) {
          onSaveToLibrary(result.data);
          setSaveAfterAccept(false);
        }
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await inspectionsApi.deleteFinding(inspectionId, finding.id);
      onDeleted(finding.id);
    } finally {
      setDeleting(false);
    }
  };

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setShowPhotoPrompt(false);
    setUploadingPhoto(true);
    try {
      const photo = await inspectionsApi.addFindingPhoto(inspectionId, finding.id, file);
      onUpdated({ ...finding, photos: [...(finding.photos ?? []), photo] });
    } catch (err) {
      console.error('Photo upload failed:', err);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photo: FindingPhoto) => {
    setDeletingPhotoId(photo.id);
    try {
      await inspectionsApi.deleteFindingPhoto(inspectionId, finding.id, photo.id);
      onUpdated({ ...finding, photos: (finding.photos ?? []).filter((p) => p.id !== photo.id) });
    } finally {
      setDeletingPhotoId(null);
    }
  };

  if (isEditing) {
    const editKey = severityKey(editSeverity);
    const editConfig = SEVERITY_CONFIG[editKey];
    return (
      <div className="relative rounded-lg border border-brand/20 bg-brand-soft/40 overflow-hidden">
        <span className={cn('absolute left-0 top-0 bottom-0 w-1', editConfig.stripe)} aria-hidden />
        <div className="p-3 pl-4 space-y-2">
          <Input
            inputSize="sm"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="Finding title"
          />
          <Textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            rows={2}
            placeholder="Description (optional)"
          />
          <div className="space-y-1.5">
            <div className="flex flex-wrap gap-1.5">
              {SEVERITY_KEYS.map((key) => {
                const isSelected = key === editKey;
                const cfg = SEVERITY_CONFIG[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setEditSeverity(key === 'observation' ? null : key)}
                    className={cn(
                      'rounded-full transition-opacity',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                      !isSelected && 'opacity-60 hover:opacity-100',
                    )}
                    aria-pressed={isSelected}
                  >
                    <Badge tone={cfg.tone} dot compact>{cfg.label}</Badge>
                  </button>
                );
              })}
            </div>
            <p className="text-caption text-ink-muted leading-snug">{editConfig.help}</p>
          </div>
          {isDraft && onSaveToLibrary && (
            <SaveToLibraryToggle
              checked={saveAfterAccept}
              onChange={setSaveAfterAccept}
            />
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!editTitle.trim()}
              loading={saving}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsEditing(false);
                setEditTitle(finding.title);
                setEditDescription(finding.description ?? '');
                setEditSeverity(finding.severity);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          'relative rounded-lg border overflow-hidden transition-opacity',
          isDraft
            ? 'border-brand/20 bg-brand-soft/40'
            : finding.isIncluded
              ? 'border-border-subtle bg-surface-1'
              : 'border-border-subtle bg-surface-2 opacity-60',
        )}
      >
        {/* Left severity stripe — maps to bucket color */}
        <span className={cn('absolute left-0 top-0 bottom-0 w-1', config.stripe)} aria-hidden />

        <div className="flex items-start gap-3 p-3 pl-4">
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-body-sm font-medium text-ink truncate">{finding.title}</p>
              <Badge tone={config.tone} dot compact title={config.help}>
                {config.label}
              </Badge>
              {isDraft ? (
                <span className="inline-flex items-center gap-1">
                  <AIDraftBadge />
                  {confidence != null && (
                    <ConfidenceDots tier={confidenceTier(confidence)} />
                  )}
                </span>
              ) : (
                finding.source !== 'manual' && (
                  <Badge tone="neutral" compact>{finding.source}</Badge>
                )
              )}
            </div>
            {finding.description && (
              <p className="text-caption text-ink-muted mt-0.5 leading-relaxed">{finding.description}</p>
            )}

            {showPhotoPrompt && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'flex items-center gap-1.5 text-caption text-brand bg-brand-soft border border-brand/20 rounded px-2 py-1 mt-1.5',
                  'hover:bg-brand-soft/70 transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                )}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Add photo?
              </button>
            )}

            {(finding.photos ?? []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {(finding.photos ?? []).map((photo) => (
                  <div key={photo.id} className="relative group w-16 h-16 shrink-0">
                    <img
                      src={photo.url}
                      alt={photo.caption ?? finding.title}
                      className="w-16 h-16 object-cover rounded-md cursor-pointer border border-border-subtle"
                      onClick={() => setLightboxUrl(photo.url)}
                    />
                    <button
                      type="button"
                      onClick={() => handleDeletePhoto(photo)}
                      disabled={deletingPhotoId === photo.id}
                      className={cn(
                        'absolute -top-2 -right-2 w-6 h-6 bg-sev-critical text-white rounded-full',
                        'items-center justify-center flex opacity-0 group-hover:opacity-100 focus:opacity-100',
                        'transition-opacity hover:bg-sev-critical-ink disabled:opacity-50 shadow',
                      )}
                      title="Remove photo"
                      aria-label="Remove photo"
                    >
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Standard actions — hidden in draft state, where Accept/Edit/Ignore
              live in a full-width row at the bottom of the card. */}
          {!isDraft && (
            <div className="flex items-center gap-0.5 shrink-0">
              <IconButton
                label={finding.isIncluded ? 'Exclude from report' : 'Include in report'}
                onClick={handleToggle}
                size="sm"
                variant="ghost"
                className={finding.isIncluded ? 'text-sev-maintenance' : 'text-ink-subtle'}
                icon={
                  <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    {finding.isIncluded ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    )}
                  </svg>
                }
              />
              <IconButton
                label="Add photo"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                size="sm"
                variant="ghost"
                className="text-ink-subtle"
                icon={
                  uploadingPhoto ? (
                    <svg className="animate-spin" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )
                }
              />
              <IconButton
                label="Edit finding"
                onClick={() => setIsEditing(true)}
                size="sm"
                variant="ghost"
                className="text-ink-subtle"
                icon={
                  <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                }
              />
              {canSaveAcceptedToLibrary && (
                <IconButton
                  label="Save to library"
                  onClick={() => onSaveToLibrary?.(finding)}
                  size="sm"
                  variant="ghost"
                  className="text-ink-subtle hover:text-brand"
                  icon={
                    <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 5v14l7-4 7 4V5a2 2 0 00-2-2H7a2 2 0 00-2 2z" />
                    </svg>
                  }
                />
              )}
              <IconButton
                label="Delete finding"
                onClick={handleDelete}
                disabled={deleting}
                size="sm"
                variant="ghost"
                className="text-ink-subtle hover:text-sev-critical"
                icon={
                  <svg fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                }
              />
            </div>
          )}
        </div>

        {/* Issue #44 — opt-in to save the field-validated finding back to the
            library in the same gesture as Accept/Edit-save. Lives just above
            the action bar so it reads as a modifier on the next tap. */}
        {isDraft && onSaveToLibrary && (
          <div className="border-t border-brand/20 px-3 py-2">
            <SaveToLibraryToggle
              checked={saveAfterAccept}
              onChange={setSaveAfterAccept}
            />
          </div>
        )}

        {/* Draft action row — Accept · Edit · Ignore. Tap targets sized for
            thumb on iOS; Accept gets visual prominence (brand color, wider
            flex). On Accept or Edit-and-save, the finding sheds its draft
            state and the row is replaced by the standard icon row above. */}
        {isDraft && (
          <div className="flex items-stretch border-t border-brand/20">
            <button
              type="button"
              onClick={handleAccept}
              disabled={accepting}
              className={cn(
                'flex-[1.4] inline-flex items-center justify-center gap-1.5 h-11',
                // brand-700 hex (not text-brand-ink, which is white) — needed
                // for contrast on the brand-soft tinted card.
                'text-sm font-semibold text-[#1A4328]',
                'hover:bg-brand-soft/80 active:bg-brand-soft transition-colors',
                'focus-visible:outline-none focus-visible:bg-brand-soft',
                'disabled:opacity-60',
              )}
              aria-label="Accept AI draft"
            >
              {accepting ? (
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" aria-hidden>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
              Accept
            </button>
            <span className="self-center w-px h-5 bg-brand/20" aria-hidden />
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className={cn(
                'flex-1 inline-flex items-center justify-center gap-1.5 h-11',
                'text-sm font-semibold text-ink-muted',
                'hover:bg-brand-soft/80 active:bg-brand-soft transition-colors',
                'focus-visible:outline-none focus-visible:bg-brand-soft',
              )}
              aria-label="Edit AI draft"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              Edit
            </button>
            <span className="self-center w-px h-5 bg-brand/20" aria-hidden />
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className={cn(
                'flex-[0.85] inline-flex items-center justify-center gap-1.5 h-11',
                'text-sm font-semibold text-ink-faint',
                'hover:bg-brand-soft/80 hover:text-ink-muted active:bg-brand-soft transition-colors',
                'focus-visible:outline-none focus-visible:bg-brand-soft',
                'disabled:opacity-60',
              )}
              aria-label="Ignore AI draft"
            >
              Ignore
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={handlePhotoSelect}
        />
      </div>

      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <img
            src={lightboxUrl}
            alt="Finding photo"
            className="max-w-full max-h-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 w-9 h-9 bg-white/20 text-white rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            aria-label="Close lightbox"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
    </>
  );
};

// ─── Confidence tier indicator ────────────────────────────────────────────────
// Three-dot signal displayed alongside the AIDraftBadge on draft findings.
// High = 3 filled dots, Medium = 2 filled + 1 muted, Low = 1 filled + 2 muted
// (amber-tinted to nudge the inspector to review carefully before accepting).

interface ConfidenceDotsProps {
  tier: 'high' | 'medium' | 'low';
}

function ConfidenceDots({ tier }: ConfidenceDotsProps) {
  const filled = tier === 'high' ? 3 : tier === 'medium' ? 2 : 1;
  const isLow = tier === 'low';
  const dotColor = isLow ? '#935E0A' : '#1A4328'; // amber on low, brand-700 otherwise
  const mutedOpacity = 0.22;

  return (
    <span
      className="inline-flex items-center gap-[2px]"
      title={
        tier === 'high'
          ? 'High confidence'
          : tier === 'medium'
          ? 'Medium confidence — review before accepting'
          : 'Low confidence — look closely before accepting'
      }
      aria-label={`${tier} confidence`}
    >
      {[0, 1, 2].map((i) => (
        <svg
          key={i}
          width="5"
          height="5"
          viewBox="0 0 5 5"
          aria-hidden
        >
          <circle
            cx="2.5"
            cy="2.5"
            r="2.5"
            fill={dotColor}
            fillOpacity={i < filled ? 1 : mutedOpacity}
          />
        </svg>
      ))}
    </span>
  );
}

// ─── Save-to-library toggle ─────────────────────────────────────────────────
// Issue #44 — used by the draft accept/edit surface so an inspector can
// promote a field-validated AI finding into a reusable library template in
// one gesture. The toggle is visual sugar around a real <input type=checkbox>
// so iOS taps and screen readers route correctly.
interface SaveToLibraryToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
}

function SaveToLibraryToggle({ checked, onChange }: SaveToLibraryToggleProps) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none text-caption text-ink-muted">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-border-subtle text-brand focus-visible:ring-2 focus-visible:ring-brand"
      />
      <span className="inline-flex items-center gap-1">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 5v14l7-4 7 4V5a2 2 0 00-2-2H7a2 2 0 00-2 2z" />
        </svg>
        Save to library after accepting
      </span>
    </label>
  );
}

export default FindingCard;
